import React, { useState, useEffect } from 'react';
import {
  tenantApi,
  Schedule,
  User,
  Group,
  EscalationPolicyDto,
  EscalationStepRequest,
  CreateEscalationPolicyRequest,
} from '../../services/tenantApi';

export const EscalationPoliciesManagement: React.FC = () => {
  const [policies, setPolicies] = useState<EscalationPolicyDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editPolicy, setEditPolicy] = useState<EscalationPolicyDto | null>(null);
  const [showGuide, setShowGuide] = useState(false);

  useEffect(() => {
    loadPolicies();
  }, []);

  const loadPolicies = async () => {
    setLoading(true);
    try {
      const data = await tenantApi.getEscalationPolicies();
      setPolicies(data);
    } catch (err) {
      console.error('Failed to load escalation policies:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (data: CreateEscalationPolicyRequest) => {
    await tenantApi.createEscalationPolicy(data);
    setShowModal(false);
    loadPolicies();
  };

  const handleUpdate = async (policyId: string, data: CreateEscalationPolicyRequest) => {
    await tenantApi.updateEscalationPolicy(policyId, data);
    setEditPolicy(null);
    loadPolicies();
  };

  const handleDelete = async (policyId: string) => {
    if (!confirm('Are you sure you want to delete this escalation policy?')) return;
    try {
      await tenantApi.deleteEscalationPolicy(policyId);
      loadPolicies();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const openEdit = (policy: EscalationPolicyDto) => {
    setEditPolicy(policy);
  };

  return (
    <div className="management-section escalation-policies-view">
      <div className="section-header">
        <h2>Escalation Policies ({policies.length})</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="btn-secondary" onClick={() => setShowGuide(!showGuide)}>
            {showGuide ? 'Hide Guide' : 'How It Works'}
          </button>
          <button className="btn-primary" onClick={() => setShowModal(true)}>
            + Add Policy
          </button>
        </div>
      </div>

      {showGuide && <EscalationGuide />}

      {loading ? (
        <div className="loading">Loading escalation policies...</div>
      ) : (
        <table className="data-table escalation-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Priority</th>
              <th>Steps</th>
              <th>Fallback</th>
              <th>Default</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {policies.map((policy) => (
              <tr key={policy.policyId}>
                <td>
                  <div>{policy.name}</div>
                  {policy.description && (
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>{policy.description}</div>
                  )}
                </td>
                <td>
                  {policy.priority ? (
                    <span className={`status-badge priority-${policy.priority.toLowerCase()}`}>
                      {policy.priority}
                    </span>
                  ) : (
                    <span style={{ color: 'var(--color-text-muted)' }}>All</span>
                  )}
                </td>
                <td>{policy.steps.length} tier{policy.steps.length !== 1 ? 's' : ''}</td>
                <td>
                  <span className={`status-badge ${policy.fallbackEnabled ? 'active' : 'inactive'}`}>
                    {policy.fallbackEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                </td>
                <td>
                  {policy.isDefault && (
                    <span className="status-badge active">Default</span>
                  )}
                </td>
                <td>
                  <button className="btn-icon escalation-action-btn" title="Edit" onClick={() => openEdit(policy)}>✏️</button>
                  <button className="btn-icon escalation-action-btn" title="Delete" onClick={() => handleDelete(policy.policyId)}>🗑️</button>
                </td>
              </tr>
            ))}
            {policies.length === 0 && (
              <tr>
                <td colSpan={6} className="empty-row">No escalation policies configured</td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {showModal && (
        <PolicyModal
          onClose={() => setShowModal(false)}
          onSave={handleCreate}
        />
      )}

      {editPolicy && (
        <PolicyModal
          policy={editPolicy}
          onClose={() => setEditPolicy(null)}
          onSave={(data) => handleUpdate(editPolicy.policyId, data)}
        />
      )}
    </div>
  );
};

// ─── Policy Create/Edit Modal ────────────────────────────────────────────

const PolicyModal: React.FC<{
  policy?: EscalationPolicyDto;
  onClose: () => void;
  onSave: (data: CreateEscalationPolicyRequest) => Promise<void>;
}> = ({ policy, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: policy?.name || '',
    description: policy?.description || '',
    priority: policy?.priority || '',
    repeatCount: policy?.repeatCount ?? 0,
    fallbackEnabled: policy?.fallbackEnabled ?? false,
    fallbackTargetType: policy?.fallbackTargetType || '',
    fallbackTargetId: policy?.fallbackTargetId || '',
    isDefault: policy?.isDefault ?? false,
  });

  const [steps, setSteps] = useState<EscalationStepRequest[]>(
    policy?.steps.map((s) => ({
      stepOrder: s.stepOrder,
      targetType: s.targetType,
      targetId: s.targetId,
      timeoutMinutes: s.timeoutMinutes,
      resolutionMinutes: s.resolutionMinutes ?? undefined,
      channels: s.channels,
    })) || [{ targetType: 'Schedule', targetId: '', timeoutMinutes: 5, channels: 'Email' }]
  );

  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSchedules();
    loadUsers();
    loadGroups();
  }, []);

  const loadSchedules = async () => {
    try {
      const data = await tenantApi.getSchedules();
      setSchedules(data);
    } catch (err) {
      console.error('Failed to load schedules:', err);
    }
  };

  const loadUsers = async () => {
    try {
      const data = await tenantApi.getUsers();
      setUsers(data.users);
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  };

  const loadGroups = async () => {
    try {
      const data = await tenantApi.getGroups();
      setGroups(data);
    } catch (err) {
      console.error('Failed to load groups:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const orderedSteps = steps.map((s, i) => ({ ...s, stepOrder: i }));
      await onSave({
        name: formData.name,
        description: formData.description || undefined,
        priority: formData.priority || undefined,
        repeatCount: formData.repeatCount,
        fallbackEnabled: formData.fallbackEnabled,
        fallbackTargetType: formData.fallbackEnabled ? formData.fallbackTargetType || undefined : undefined,
        fallbackTargetId: formData.fallbackEnabled ? formData.fallbackTargetId || undefined : undefined,
        isDefault: formData.isDefault,
        steps: orderedSteps,
      });
    } catch (err: any) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  const addStep = () => {
    setSteps([...steps, { targetType: 'Schedule', targetId: '', timeoutMinutes: 10, channels: 'Email' }]);
  };

  const removeStep = (index: number) => {
    if (steps.length <= 1) return;
    setSteps(steps.filter((_, i) => i !== index));
  };

  const updateStep = (index: number, field: keyof EscalationStepRequest, value: any) => {
    setSteps(steps.map((s, i) => (i === index ? { ...s, [field]: value } : s)));
  };

  const moveStep = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= steps.length) return;
    const newSteps = [...steps];
    [newSteps[index], newSteps[newIndex]] = [newSteps[newIndex], newSteps[index]];
    setSteps(newSteps);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg modal-scroll" onClick={(e) => e.stopPropagation()}>
        <h3>{policy ? 'Edit Escalation Policy' : 'Create Escalation Policy'}</h3>
        <form onSubmit={handleSubmit}>
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label>Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="e.g., Default Escalation"
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of this policy"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Priority Filter</label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              >
                <option value="">All Priorities (Default)</option>
                <option value="P1">P1 - Critical</option>
                <option value="P2">P2 - High</option>
                <option value="P3">P3 - Medium</option>
                <option value="P4">P4 - Low</option>
                <option value="P5">P5 - Informational</option>
              </select>
              <p className="help-text">Leave empty for a default policy that applies to all priorities</p>
            </div>

            <div className="form-group">
              <label>Repeat Count</label>
              <input
                type="number"
                min="0"
                max="10"
                value={formData.repeatCount}
                onChange={(e) => setFormData({ ...formData, repeatCount: parseInt(e.target.value) || 0 })}
              />
              <p className="help-text">Number of times to repeat the escalation chain (0 = no repeat)</p>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.isDefault}
                  onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                />
                {' '}Set as default policy
              </label>
            </div>
          </div>

          {/* Steps Builder */}
          <div className="form-group">
            <label>Escalation Steps *</label>
            <p className="help-text">Define the escalation tiers. Each step is tried in order with its timeout.</p>
            <div className="steps-builder">
              {steps.map((step, index) => (
                <div key={index} className="step-row">
                  <div className="step-header">
                    <span className="step-label">Tier {index}</span>
                    <div className="step-actions">
                      <button type="button" className="btn-icon" title="Move Up" onClick={() => moveStep(index, 'up')} disabled={index === 0}>
                        ▲
                      </button>
                      <button type="button" className="btn-icon" title="Move Down" onClick={() => moveStep(index, 'down')} disabled={index === steps.length - 1}>
                        ▼
                      </button>
                      <button type="button" className="btn-icon" title="Remove" onClick={() => removeStep(index)} disabled={steps.length <= 1}>
                        ✕
                      </button>
                    </div>
                  </div>
                  <div className="step-fields">
                    <div className="form-group">
                      <label>Target Type</label>
                      <select
                        value={step.targetType}
                        onChange={(e) => updateStep(index, 'targetType', e.target.value)}
                      >
                        <option value="Schedule">Schedule</option>
                        <option value="User">User</option>
                        <option value="Group">Group</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Target</label>
                      {step.targetType === 'Schedule' ? (
                        <select
                          value={step.targetId}
                          onChange={(e) => updateStep(index, 'targetId', e.target.value)}
                          required
                        >
                          <option value="">Select schedule...</option>
                          {schedules.map((s) => (
                            <option key={s.scheduleId} value={s.scheduleId}>
                              {s.name}
                            </option>
                          ))}
                        </select>
                      ) : step.targetType === 'User' ? (
                        <select
                          value={step.targetId}
                          onChange={(e) => updateStep(index, 'targetId', e.target.value)}
                          required
                        >
                          <option value="">Select user...</option>
                          {users.map((u) => (
                            <option key={u.userId} value={u.userId}>
                              {u.displayName} ({u.email})
                            </option>
                          ))}
                        </select>
                      ) : step.targetType === 'Group' ? (
                        <select
                          value={step.targetId}
                          onChange={(e) => updateStep(index, 'targetId', e.target.value)}
                          required
                        >
                          <option value="">Select group...</option>
                          {groups.map((g) => (
                            <option key={g.groupId} value={g.groupId}>
                              {g.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={step.targetId}
                          onChange={(e) => updateStep(index, 'targetId', e.target.value)}
                          placeholder="Target ID"
                          required
                        />
                      )}
                    </div>
                    <div className="form-group">
                      <label>Timeout (min)</label>
                      <input
                        type="number"
                        min="1"
                        max="1440"
                        value={step.timeoutMinutes ?? 5}
                        onChange={(e) => updateStep(index, 'timeoutMinutes', parseInt(e.target.value) || 5)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Resolution (min)</label>
                      <input
                        type="number"
                        min="0"
                        max="1440"
                        value={step.resolutionMinutes ?? ''}
                        onChange={(e) => updateStep(index, 'resolutionMinutes', e.target.value ? parseInt(e.target.value) : undefined)}
                        placeholder="Optional"
                      />
                    </div>
                    <div className="form-group">
                      <label>Channels</label>
                      <select
                        value={step.channels || 'Email'}
                        onChange={(e) => updateStep(index, 'channels', e.target.value)}
                      >
                        <option value="Email">Email</option>
                        <option value="SMS">SMS</option>
                        <option value="Email,SMS">Email + SMS</option>
                        <option value="Push">Push</option>
                        <option value="Email,SMS,Push">All</option>
                      </select>
                    </div>
                  </div>
                </div>
              ))}
              <button type="button" className="btn-secondary" onClick={addStep} style={{ marginTop: '0.5rem' }}>
                + Add Tier
              </button>
            </div>
          </div>

          {/* Fallback */}
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.fallbackEnabled}
                onChange={(e) => setFormData({ ...formData, fallbackEnabled: e.target.checked })}
              />
              {' '}Enable fallback (final tier if all steps exhausted)
            </label>
            {formData.fallbackEnabled && (
              <div className="form-row" style={{ marginTop: '0.5rem' }}>
                <div className="form-group">
                  <label>Fallback Target Type</label>
                  <select
                    value={formData.fallbackTargetType}
                    onChange={(e) => setFormData({ ...formData, fallbackTargetType: e.target.value })}
                  >
                    <option value="">Select...</option>
                    <option value="Schedule">Schedule</option>
                    <option value="User">User</option>
                    <option value="Group">Group</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Fallback Target</label>
                  {formData.fallbackTargetType === 'Schedule' ? (
                    <select
                      value={formData.fallbackTargetId}
                      onChange={(e) => setFormData({ ...formData, fallbackTargetId: e.target.value })}
                    >
                      <option value="">Select schedule...</option>
                      {schedules.map((s) => (
                        <option key={s.scheduleId} value={s.scheduleId}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  ) : formData.fallbackTargetType === 'User' ? (
                    <select
                      value={formData.fallbackTargetId}
                      onChange={(e) => setFormData({ ...formData, fallbackTargetId: e.target.value })}
                    >
                      <option value="">Select user...</option>
                      {users.map((u) => (
                        <option key={u.userId} value={u.userId}>
                          {u.displayName} ({u.email})
                        </option>
                      ))}
                    </select>
                  ) : formData.fallbackTargetType === 'Group' ? (
                    <select
                      value={formData.fallbackTargetId}
                      onChange={(e) => setFormData({ ...formData, fallbackTargetId: e.target.value })}
                    >
                      <option value="">Select group...</option>
                      {groups.map((g) => (
                        <option key={g.groupId} value={g.groupId}>
                          {g.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={formData.fallbackTargetId}
                      onChange={(e) => setFormData({ ...formData, fallbackTargetId: e.target.value })}
                      placeholder="Select a target type first"
                      disabled
                    />
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="form-actions">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? 'Saving...' : policy ? 'Save Changes' : 'Create Policy'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Inline Guide ────────────────────────────────────────────────────────

const EscalationGuide: React.FC = () => (
  <div className="escalation-guide">

    <div className="guide-section">
      <h3>How Escalation Works</h3>
      <p>
        When an alert arrives, Zurvit matches it to an escalation policy based on the alert's <strong>priority</strong>.
        If no priority-specific policy exists, the <strong>default policy</strong> is used.
        The system then works through the policy's tiers in order, notifying the designated person or team at each step.
      </p>
      <div className="guide-callout">
        <strong>Automatic escalation:</strong> If an alert is not acknowledged within a tier's timeout,
        the system automatically advances to the next tier and notifies the next target.
        This continues until someone responds or all tiers are exhausted.
      </div>
    </div>

    <div className="guide-section">
      <h3>Two Escalation Modes</h3>
      <p>
        Each escalation operates in one of two modes, which transition automatically:
      </p>
      <table className="guide-table">
        <thead>
          <tr>
            <th>Mode</th>
            <th>Active When</th>
            <th>Timer Uses</th>
            <th>Stops When</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>First Touch</strong></td>
            <td>Alert is Open (unacknowledged)</td>
            <td>Timeout (minutes)</td>
            <td>Someone acknowledges or closes the alert</td>
          </tr>
          <tr>
            <td><strong>Resolution</strong></td>
            <td>Alert is Acknowledged but not yet closed</td>
            <td>Resolution SLA (minutes)</td>
            <td>Someone closes the alert</td>
          </tr>
        </tbody>
      </table>
      <p className="help-text" style={{ marginTop: '0.5rem' }}>
        When a tier has a <strong>Resolution SLA</strong> set, acknowledging the alert does not stop escalation --
        it switches to Resolution mode and keeps the timer running until the alert is closed.
        Leave the Resolution SLA empty if you want acknowledgment to stop escalation.
      </p>
    </div>

    <div className="guide-section">
      <h3>Creating a Policy</h3>
      <div className="guide-steps">
        <div className="guide-step">
          <div className="guide-step-number">1</div>
          <div className="guide-step-content">
            <h4>Name and Priority</h4>
            <p>
              Give the policy a descriptive name.
              Optionally set a <strong>Priority Filter</strong> to restrict it to a specific alert priority (P1--P5).
              Only one policy per priority is allowed. A policy with no priority filter and the
              <strong> Default</strong> flag serves as the catch-all.
            </p>
          </div>
        </div>
        <div className="guide-step">
          <div className="guide-step-number">2</div>
          <div className="guide-step-content">
            <h4>Define Escalation Tiers</h4>
            <p>
              Add one or more tiers. Each tier specifies <strong>who</strong> to notify,
              <strong> how</strong> to notify them, and <strong>how long</strong> to wait before escalating further.
            </p>
            <table className="guide-table">
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Target Type</strong></td>
                  <td>
                    <strong>Schedule</strong> -- notifies whoever is currently on-call (dynamic, follows rotation).
                    <strong> User</strong> -- always notifies a specific named person.
                    <strong> Group</strong> -- notifies all members of the group.
                  </td>
                </tr>
                <tr>
                  <td><strong>Target</strong></td>
                  <td>The specific schedule, user, or group to notify at this tier.</td>
                </tr>
                <tr>
                  <td><strong>Timeout</strong></td>
                  <td>Minutes to wait (in First Touch mode) before escalating to the next tier. Range: 1--1440.</td>
                </tr>
                <tr>
                  <td><strong>Resolution SLA</strong></td>
                  <td>Optional. If set, after acknowledgment the timer continues in Resolution mode using this value. Leave empty to stop escalation on acknowledgment.</td>
                </tr>
                <tr>
                  <td><strong>Channels</strong></td>
                  <td>How to send the notification: Email, SMS, or both.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div className="guide-step">
          <div className="guide-step-number">3</div>
          <div className="guide-step-content">
            <h4>Configure Fallback (Optional)</h4>
            <p>
              Enable <strong>Fallback</strong> to define a safety net for when all tiers are exhausted without resolution.
              The fallback target receives a final notification. Without fallback, escalation simply ends after the last tier.
            </p>
          </div>
        </div>
      </div>
    </div>

    <div className="guide-section">
      <h3>Priority Changes</h3>
      <p>
        When an alert's priority changes, escalation is automatically re-evaluated:
      </p>
      <table className="guide-table">
        <thead>
          <tr>
            <th>Situation</th>
            <th>What Happens</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Alert is <strong>Open</strong></td>
            <td>Escalation resets to Tier 0 of the new priority's policy.</td>
          </tr>
          <tr>
            <td>Alert is <strong>Acknowledged</strong> + new policy has Resolution SLA</td>
            <td>Switches to Resolution mode at the first tier with a Resolution SLA.</td>
          </tr>
          <tr>
            <td>Alert is <strong>Acknowledged</strong> + no Resolution SLA</td>
            <td>Escalation completes (acknowledgment already covers it).</td>
          </tr>
          <tr>
            <td>No policy matches the new priority</td>
            <td>Any active escalation is completed.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div className="guide-section">
      <h3>Example: Simple On-Call Escalation</h3>
      <div className="guide-callout guide-callout-example">
        <p><strong>Goal:</strong> Notify the on-call engineer. If no response in 10 minutes, notify the team lead.</p>
        <table className="guide-table">
          <thead>
            <tr>
              <th>Tier</th>
              <th>Target</th>
              <th>Timeout</th>
              <th>Channels</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>0</td>
              <td>Schedule: Primary On-Call</td>
              <td>10 min</td>
              <td>Email + SMS</td>
            </tr>
            <tr>
              <td>1</td>
              <td>User: Jane (Team Lead)</td>
              <td>15 min</td>
              <td>Email + SMS</td>
            </tr>
          </tbody>
        </table>
        <p className="help-text" style={{ marginTop: '0.5rem' }}>
          Alert arrives -- Tier 0 notifies on-call via Email + SMS.
          After 10 min with no acknowledgment, Tier 1 notifies Jane.
          If Jane acknowledges, escalation stops.
        </p>
      </div>
    </div>

    <div className="guide-section">
      <h3>Example: Critical Alert with Resolution SLA</h3>
      <div className="guide-callout guide-callout-example">
        <p><strong>Goal:</strong> P1 alerts need fast acknowledgment AND fast resolution. Keep escalating even after acknowledgment.</p>
        <table className="guide-table">
          <thead>
            <tr>
              <th>Tier</th>
              <th>Target</th>
              <th>Timeout</th>
              <th>Resolution SLA</th>
              <th>Channels</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>0</td>
              <td>Schedule: Primary On-Call</td>
              <td>5 min</td>
              <td>30 min</td>
              <td>Email + SMS</td>
            </tr>
            <tr>
              <td>1</td>
              <td>Group: Senior Engineers</td>
              <td>10 min</td>
              <td>60 min</td>
              <td>Email + SMS</td>
            </tr>
            <tr>
              <td>2</td>
              <td>User: VP Engineering</td>
              <td>15 min</td>
              <td>--</td>
              <td>Email + SMS</td>
            </tr>
          </tbody>
        </table>
        <p className="help-text" style={{ marginTop: '0.5rem' }}>
          On-call acknowledges at 3 min -- First Touch stops, Resolution mode starts (30 min timer).
          If not closed in 30 min, Senior Engineers are notified (60 min resolution timer).
          VP Engineering is the final escalation -- no Resolution SLA, so closing the alert or exhausting the timeout ends escalation.
        </p>
      </div>
    </div>

    <div className="guide-section">
      <h3>Best Practices</h3>
      <ul className="guide-tips">
        <li><strong>Always have a default policy</strong> -- without one, alerts that don't match a priority-specific policy will have no escalation.</li>
        <li><strong>Use Schedules for Tier 0</strong> -- this ensures the current on-call person is always notified first, even when rotations change.</li>
        <li><strong>Use specific Users for management tiers</strong> -- named users for later tiers ensure the right manager is always in the loop.</li>
        <li><strong>Set Resolution SLAs on critical tiers</strong> -- for P1/P2 alerts, don't let escalation stop just because someone acknowledged.</li>
        <li><strong>Keep Tier 0 timeouts short</strong> -- 5 to 10 minutes is typical for initial response.</li>
        <li><strong>Enable fallback on critical policies</strong> -- ensures someone is always notified, even after all tiers time out.</li>
      </ul>
    </div>

  </div>
);
